import { useState } from "react";

import { useLingui } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { Globe } from "lucide-react";

import { Button } from "@/components/ui/button";
import { setLocale, type SupportedLocale } from "@/i18n/i18n";
import { useActiveLocales } from "@/lib/hooks/use-platform-config";

const LOCALE_LABEL: Record<SupportedLocale, string> = {
  en: "English",
  ro: "Română",
  bg: "Български",
  de: "Deutsch",
  fr: "Français",
  hu: "Magyar",
  it: "Italiano",
  el: "Ελληνικά",
  hr: "Hrvatski",
  sr: "Српски",
  cs: "Čeština",
  es: "Español",
  pl: "Polski",
  tr: "Türkçe",
  uk: "Українська",
};

export const LOCALE_FLAG: Record<SupportedLocale, string> = {
  en: "🇬🇧",
  ro: "🇷🇴",
  bg: "🇧🇬",
  de: "🇩🇪",
  fr: "🇫🇷",
  hu: "🇭🇺",
  it: "🇮🇹",
  el: "🇬🇷",
  hr: "🇭🇷",
  sr: "🇷🇸",
  cs: "🇨🇿",
  es: "🇪🇸",
  pl: "🇵🇱",
  tr: "🇹🇷",
  uk: "🇺🇦",
};

interface LocaleSwitcherProps {
  /** "icon" = Globe icon button (default, used in headers).
   *  "inline" = flag + uppercase code text trigger (used in footer).
   *  "named" = bordered pill with flag + native name + chevron (workspace footer). */
  variant?: "icon" | "inline" | "named";
  /** Dropdown opening direction. Use "up" when placed near the bottom of the viewport. */
  direction?: "up" | "down";
  /** Dropdown alignment relative to the trigger. */
  align?: "left" | "right";
}

export function LocaleSwitcher({
  variant = "icon",
  direction = "down",
  align = "right",
}: LocaleSwitcherProps = {}) {
  const { i18n, t } = useLingui();
  const [open, setOpen] = useState(false);
  const active = i18n.locale as SupportedLocale;
  const activeLocales = useActiveLocales();

  return (
    <div className="relative">
      {variant === "inline" ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-1 text-xs transition-colors"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t`Change language`}
        >
          <span className="text-base leading-none" aria-hidden>
            {LOCALE_FLAG[active] ?? "🌐"}
          </span>
          <span className="uppercase">{active}</span>
        </button>
      ) : variant === "named" ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="border-border/60 hover:border-border bg-background text-foreground inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm transition-colors"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t`Change language`}
        >
          <span className="text-base leading-none" aria-hidden>
            {LOCALE_FLAG[active] ?? "🌐"}
          </span>
          <span className="text-sm font-medium">{LOCALE_LABEL[active] ?? active}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
            className={cn("text-muted-foreground transition-transform", open && "rotate-180")}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          // 44px tap target on touch (P19); compact on desktop.
          className="size-11 lg:size-9"
          aria-label={t`Change language`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <Globe className="size-5" aria-hidden />
          <span className="sr-only">{LOCALE_LABEL[active] ?? active}</span>
        </Button>
      )}

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              "border-border bg-card text-card-foreground absolute z-50 w-44 overflow-hidden rounded-lg border shadow-lg",
              align === "left" ? "left-0" : "right-0",
              direction === "up" ? "bottom-full mb-1" : "top-full mt-1",
            )}
          >
            <ul className="max-h-72 overflow-y-auto py-1">
              {activeLocales.map((locale) => (
                <li key={locale}>
                  <button
                    type="button"
                    onClick={async () => {
                      await setLocale(locale);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                      active === locale
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                    )}
                  >
                    <span className="text-base leading-none" aria-hidden>
                      {LOCALE_FLAG[locale]}
                    </span>
                    <span className="flex-1">{LOCALE_LABEL[locale]}</span>
                    <span className="text-muted-foreground/60 font-mono text-[0.6875rem] uppercase">
                      {locale}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
